// 2026-07-23 - FormuLearning - 용액별 용출프로파일 비교 그래프 (이슈 #50 S-2, P5 선행)
// 2026-08-03 수정 - 이슈 #52: **시점 오름차순 정렬** 후 그린다. plotly는 배열 순서대로
//                   점을 잇기 때문에 저장 순서가 `10,15,30,45,60,0`이면 마지막 점에서
//                   0분으로 되짚는 직선이 생긴다(260803 사용자 보고, 6/24 amiodarone).
// 2026-08-03 수정 - 이슈 #53: 세로축 상한을 **기본 100 + 데이터 따라 확장**으로. 종전
//                   `[0,105]` 고정에서는 100% 초과 실측값(102.9·107.8·109.6)이 잘렸다.
// 2026-08-14 수정 - 이슈 #60(M84-20): 용액 매칭이 **문자열 완전 일치**여서 대조약 `"1.2"`와
//                   시험약 `"pH 1.2"`가 어긋나면 시험 곡선이 빠진 빈 축이 남았다. 백엔드
//                   f2(`solution_key`)와 같은 정규화 규칙으로 맞춘다.
// 2026-08-31 수정 - 이슈 #58 S5 3차(#68, M84-15): 용액 제목에 **반복 횟수**를 붙인다
//                   (`pH 1.2 (n=3)`). 값은 시험약 프로파일의 `n` 이며 미입력이면 붙이지 않는다.
//                   0 시점은 표에서만 감추고(M84-16) **그래프에는 그대로 그린다.**
// 2026-09-21 수정 - 이슈 #69(S6, UI/UX v2.2 COMP-DISSOLUTION-COMPARE-CHART):
//                   ① 세로 나열 → **용액 수만큼 가로 배치**(좁으면 줄바꿈, M84-23·65)
//                   ② 각 포인트 **±1σ**(SD) · 0 시점이 없으면 **원점 보충**(M84-24)
//                   ③ 범례 `Reference AVG±SD` / `Test AVG±SD`(M84-65)
//                   ④ 그래프 아래 **용액별 f2 + 판정**(M84-25·65). 판정은 서버 값을 쓴다.
//                   대조약 프로파일은 이제 **분석 응답**(`F2Analysis`)에서 온다 — 종전에는 활성
//                   Lot 을 겹쳐 그려 f2 기준(run 시점 대조약)과 다른 곡선이 보일 수 있었다.
//                   용액 키 정규화는 `lib/dissolution.ts` 로 옮겼다.
// 파일 위치: frontend/src/features/step4/DissolutionCompareChart.tsx
// 목적: 선택 배치의 용액(pH)별 용출 곡선(time×용출률%)을 가로로 늘어놓고, 분석 기준
//       대조약 프로파일을 오버레이한다 — f2 값의 근거를 눈으로 확인(BR-S3-L07, UI/UX §5.8).

import { useEffect, useRef } from 'react'
import type { DissolutionProfile, PhProfile } from '../../api/step3'
import { useUiStore } from '../../stores/uiStore'
import { chartTokens } from '../../lib/chartTheme'
import { F2_REASON_TEXT, solutionKey } from '../../lib/dissolution'

const PLOT_OPTS = { displayModeBar: false, responsive: true }

interface Row { time: number; dissolution: number; sd: number }

/** 시점 오름차순 정렬 — 저장 배열 순서에 그림이 좌우되지 않게 한다(#52). */
const byTime = (rows: Row[]): Row[] => [...rows].sort((a, b) => a.time - b.time)

/**
 * 0 시점 보충(M84-24 «0 포함») — 0분 행이 없으면 원점(0분·0%)을 앞에 둔다. 0분
 * 용출률은 정의상 0 이다. 그리기 전용이며 f2 계산과 무관하다(엔진이 0분을 원래 제외).
 */
const withOrigin = (rows: Row[]): Row[] =>
  rows.length === 0 || rows.some((d) => d.time === 0) ? rows : [{ time: 0, dissolution: 0, sd: 0 }, ...rows]

const toRows = (rows: { time: number; dissolution: number | null; sd?: number | null }[] | undefined): Row[] =>
  withOrigin(byTime((rows || [])
    .filter((d) => d.dissolution != null && Number.isFinite(d.time))
    .map((d) => ({ time: d.time, dissolution: d.dissolution as number, sd: Number(d.sd) || 0 }))))

/**
 * 세로축 상한 — **기본 100**, 실측(+1σ)이 그보다 크면 데이터에 맞춰 넓힌다(#53).
 * 오차 막대 끝이 잘리지 않게 SD 를 더한 값으로 잰다. 눈금 위 여유 5%p, 5 단위 올림.
 */
const Y_BASE = 100
const yMax = (...rows: Row[][]): number => {
  const peak = Math.max(...rows.flat().map((d) => d.dissolution + d.sd).filter(Number.isFinite), 0)
  return peak <= Y_BASE ? Y_BASE + 5 : Math.ceil((peak + 5) / 5) * 5
}

interface Props {
  batchLabel: string
  /** 선택 배치의 시험 프로파일. */
  profiles: DissolutionProfile[]
  /** 분석 기준 대조약 프로파일(`F2Analysis.reference_profiles`). */
  referenceProfiles: PhProfile[]
  /** 용액별 f2 · 판정 — 서버 분석 응답 그대로(`f2_by_solution` · `similar_by_solution`). */
  f2BySolution: Record<string, number>
  similarBySolution: Record<string, boolean>
  /** 전체 f2 가 없을 때의 사유 코드(제외 배치 `excluded` 등). */
  f2Reason: string | null
}

/** 하나의 용액(pH) 비교 차트. (prop명 `ref`는 React 예약어라 refRows 사용) */
function SolutionChart({ ph, n, batchLabel, test, refRows }: {
  ph: string
  /** 반복 횟수(M84-15) — 제목에 `pH 1.2 (n=3)` 로 붙는다. 미입력이면 붙이지 않는다. */
  n: number | null
  batchLabel: string
  test: Row[]
  refRows: Row[]
}) {
  const theme = useUiStore((s) => s.theme)
  const div = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!div.current) return
    let cancelled = false
    import('plotly.js-dist-min').then((Plotly) => {
      if (cancelled || !div.current) return
      const t = chartTokens()
      const trace = (rows: Row[], name: string, color: string, dash: string, who: string) => ({
        x: rows.map((d) => d.time), y: rows.map((d) => d.dissolution),
        customdata: rows.map((d) => d.sd),
        type: 'scatter', mode: 'lines+markers', name,
        line: { color, width: 2, dash }, marker: { color, size: 6 },
        // ±1σ(M84-24) — SD 0 인 점(원점 보충 포함)은 막대 없이 점만 남는다. 실측 SD 는 1~2%p
        // 수준이라 막대가 마커에 묻히기 쉽다 — 끝 가로선을 마커보다 넓게 둬 작은 SD 도 보이게 한다
        error_y: { type: 'data', array: rows.map((d) => d.sd), visible: true, color, thickness: 1.2, width: 6 },
        hovertemplate: `${who} %{x}min<br>%{y:.1f}% ± %{customdata:.1f}<extra></extra>`,
      })
      const traces: Record<string, unknown>[] = []
      if (refRows.length > 0) traces.push(trace(refRows, 'Reference AVG±SD', t.purple, 'dash', 'Reference'))
      if (test.length > 0) traces.push(trace(test, 'Test AVG±SD', t.teal, 'solid', batchLabel))
      Plotly.newPlot(div.current, traces, {
        paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: t.plotBg,
        font: { color: t.font, size: 9 },
        margin: { t: 40, r: 10, b: 30, l: 35 },
        title: { text: n != null ? `pH ${ph} (n=${n})` : `pH ${ph}`, font: { size: 10, color: t.font }, x: 0, y: 0.97 },
        xaxis: { gridcolor: t.grid, title: { text: 'min', font: { size: 9 } }, rangemode: 'tozero' },
        yaxis: {
          gridcolor: t.grid, title: { text: '%', font: { size: 9 } },
          range: [0, yMax(refRows, test)],
        },
        showlegend: true,
        legend: { orientation: 'h', y: 1.16, x: 1, xanchor: 'right', font: { size: 8 } },
      }, PLOT_OPTS)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ph, n, batchLabel, JSON.stringify(test), JSON.stringify(refRows), theme])

  return <div ref={div} style={{ width: '100%', height: 210 }} />
}

/** 그래프 아래 «용액별 f2 + 판정»(M84-25·65). 판정은 서버가 내린 값이다. */
function F2Verdict({ solution, value, similar, reason }: {
  solution: string
  value: number | undefined
  similar: boolean | undefined
  reason: string | null
}) {
  if (value == null) {
    return (
      <p className="mt-0.5 text-center text-[10px] text-text-muted" data-testid={`f2-verdict-${solution}`}
         title={reason ? F2_REASON_TEXT[reason] ?? reason : undefined}>
        f2 —{reason === 'excluded' ? ' (제외 배치)' : ''}
      </p>
    )
  }
  return (
    <p className="mt-0.5 flex items-center justify-center gap-1.5 text-[11px]" data-testid={`f2-verdict-${solution}`}>
      <span className="text-text-secondary">f2</span>
      <b className="text-text-body" data-testid={`f2-value-${solution}`}>{value.toFixed(1)}</b>
      <span
        className="rounded border px-1.5 py-0.5 text-[10px] font-semibold"
        data-testid={`f2-judge-${solution}`}
        data-similar={similar ? 'true' : 'false'}
        style={similar
          ? { borderColor: 'var(--border-teal)', background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }
          : { borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}
      >
        {similar ? '✓ 유사 (Similar)' : '✕ 비유사 (Dissimilar)'}
      </span>
    </p>
  )
}

export default function DissolutionCompareChart({
  batchLabel, profiles, referenceProfiles, f2BySolution, similarBySolution, f2Reason,
}: Props) {
  // 용액 목록은 대조약 격자 기준(Lot 격자가 정본), 배치 프로파일은 pH로 매칭한다.
  const solutions = referenceProfiles.length > 0
    ? referenceProfiles.map((p) => p.ph)
    : profiles.map((p) => p.ph)
  if (solutions.length === 0) return null

  return (
    // 용액 수만큼 가로 배치(M84-23·65) — 한 칸 최소 폭을 두어 좁은 화면에서는 줄바꿈한다
    <div
      className="mt-2 grid gap-2"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}
      data-testid="dissolution-compare-grid"
    >
      {solutions.map((ph) => {
        // 표기 차이에 견디는 매칭(M84-20) — `"1.2"` ↔ `"pH 1.2"`가 같은 용액으로 잡힌다.
        const key = solutionKey(ph)
        const refRows = toRows(referenceProfiles.find((p) => solutionKey(p.ph) === key)?.time_points)
        const testProfile = profiles.find((p) => solutionKey(p.ph) === key)
        const testRows = toRows(testProfile?.data)
        return (
          <div key={ph} data-testid={`dissolution-compare-${key}`}>
            {/* 반복 횟수는 **시험약 프로파일**이 진다(M84-15) — 제목에 `pH 1.2 (n=3)` */}
            <SolutionChart ph={ph} n={testProfile?.n ?? null} batchLabel={batchLabel}
                           test={testRows} refRows={refRows} />
            <F2Verdict solution={key} value={f2BySolution[key]} similar={similarBySolution[key]}
                       reason={f2BySolution[key] == null ? f2Reason : null} />
          </div>
        )
      })}
    </div>
  )
}
